import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { wiringValue } from '../../../types/subscription';

// What a concept test runs against when this account has no configuration of its own.
const DEFAULT_CONCEPT_POPULATION = 'population_vitamin_survey_enriched_copy';
import { useAuth } from '../../../context/AuthContext';
import { useSubscription } from '../../../context/subscription/SubscriptionContext';
import { injectVctStyles, ConceptSampleModal, ConceptManualModal, CONCEPT_TEMPLATE, isConceptPlaceholder } from './ConceptCardShared';
import { fetchPopulationSegments, fetchFilterCount, type VitaminSegment } from '../../../services/vitaminSegmentsService';
import {
  MAX_UPLOAD_SIZE_MB,
  ACCEPTED_IMAGE_INPUT,
  UPLOAD_HINT_TEXT,
  imageRejectionReason,
} from './conceptImageFormats';
import { resolvePopulationSelection } from './populationSelection';
import { populationLabelFor } from './populationLabel';

/* ════════════════════════════════════════════════════════════════════════════
   VITAMIN CONCEPT TEST FORM
   Ported from: /home/ec2-user/concept-test-source/
   - styles.css (1236 lines)
   - screens.jsx (617 lines)
   - app.jsx (289 lines)
   - icons.jsx (81 lines)
   ════════════════════════════════════════════════════════════════════════════ */

const API_URL = import.meta.env.VITE_API_URL;

// Ceiling for a single /parse-image request. A hung request never settles on its
// own, and while it's "parsing" the navigation gate blocks the user — so cap it and
// fall through to the fallback auto-fill (which clears the parsing state).
const CONCEPT_PARSE_TIMEOUT_MS = 60_000;

// Dev-environment gate: true for local Vite dev and the dev deployment (VITE_API_URL on
// dev.blue-pill.ai). Exposes experimental admin toggles on dev only — the production
// deployment points VITE_API_URL elsewhere, so they stay hidden there. UX gate only, not a
// security boundary: the backend independently validates the flag.
const IS_DEV_ENV = import.meta.env.DEV || (import.meta.env.VITE_API_URL || '').includes('dev.blue-pill.ai');

// Brand-neutral, breakfast-relevant study-name example for the marketplace.
const STUDY_NAME_EXAMPLE_PLACEHOLDER = 'e.g. Overnight Oats Cups — Launch Wave 1';

// A concept only counts as valid when it has content beyond the unedited
// "write manually" template (#72).
const hasRealConceptContent = (conceptStr: string): boolean =>
  conceptStr.trim() !== '' && !isConceptPlaceholder(conceptStr);


/* ═══════════════════════════════════════════════════════════════════════════
   ICONS (from icons.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const Icon = {
  arrowRight: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  arrowLeft: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M11 5l-7 7 7 7" />
    </svg>
  ),
  check: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  chevronUp: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 15l-6-6-6 6" />
    </svg>
  ),
  chevronDown: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  upload: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
    </svg>
  ),
  fileText: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </svg>
  ),
  x: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  edit: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  sparkles: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3zM19 14l.8 2.4L22 17l-2.2.6L19 20l-.8-2.4L16 17l2.2-.6L19 14zM5 14l.8 2.4L8 17l-2.2.6L5 20l-.8-2.4L2 17l2.2-.6L5 14z" />
    </svg>
  ),
  clock: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  alert: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  ),
  settings: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  plus: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  image: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
};

/* ═══════════════════════════════════════════════════════════════════════════
   SEGMENT OPTIONS & CONCEPT TEMPLATE
   ═══════════════════════════════════════════════════════════════════════════ */

interface SegmentOption {
  name: string;
  description: string;
  profiles: number;
}

const VITAMIN_SEGMENT_OPTIONS: SegmentOption[] = [
  {
    name: "Moms with Kids (4-12)",
    description: "Mothers whose children are exclusively school-aged (4-12).",
    profiles: 59
  },
  {
    name: "Moms with Teens (13-19)",
    description: "Mothers whose children are exclusively adolescents aged 13-19.",
    profiles: 21
  },
  {
    name: "Moms with Kids and Teens",
    description: "Mothers with children in BOTH age groups (4-12 and 13-19).",
    profiles: 21
  },
  {
    name: "Teens (13-19)",
    description: "Teenagers aged 13-19 who take vitamins themselves.",
    profiles: 25
  }
];

// White Pony dog food segment options
const DOG_FOOD_SEGMENT_OPTIONS: SegmentOption[] = [
  {
    name: "Steady Providers",
    description: "Reliable dog owners focused on consistent, quality nutrition.",
    profiles: 85
  },
  {
    name: "Settled Guardians",
    description: "Established pet parents with stable routines and preferences.",
    profiles: 74
  },
  {
    name: "Devoted Strivers",
    description: "Dedicated owners actively seeking the best for their dogs.",
    profiles: 42
  },
  {
    name: "Vigilant Curators",
    description: "Careful selectors who scrutinize ingredients and sourcing.",
    profiles: 35
  },
  {
    name: "Research-Driven Optimizers",
    description: "Data-oriented owners who optimize based on research and reviews.",
    profiles: 17
  }
];

// White Pony cat food segment options
const CAT_FOOD_SEGMENT_OPTIONS: SegmentOption[] = [
  {
    name: "Loving People Under Pressure",
    description: "They know what good looks like and they know they're not delivering it. The gap is constraint, not ignorance — budget squeeze plus the fear that a premium experiment ends in a refused bowl.",
    profiles: 64
  },
  {
    name: "People Who Are Happy Enough",
    description: "Settled competence. They figured out what works, filed it under 'solved,' and moved on. The cat is loved but kept in perspective: a beloved pet, not an identity.",
    profiles: 58
  },
  {
    name: "People Who Trust and Move On",
    description: "Competent delegators who treat cat ownership the way they treat their careers: find the right authority, follow the protocol, don't second-guess. The vet sets the frame.",
    profiles: 57
  },
  {
    name: "People Who Love Through Quality",
    description: "Feeding is a moral obligation and the daily proof of competent love. They distrust the industry, claim protective authority over the bowl, and stay in permanent research mode.",
    profiles: 40
  },
  {
    name: "Quietly Competent People",
    description: "Did the homework once, found something defensible, and moved on. Affluent and educated enough to know better options exist — and they've deliberately chosen not to.",
    profiles: 31
  }
];

// KNF (Kettle & Fire) segment options - Bone Broth Consumers
const KNF_SEGMENT_OPTIONS: SegmentOption[] = [
  {
    name: "Clean Gatekeepers",
    description: "They flip every package, scan every ingredient, and refuse anything they can't pronounce — that vigilance is the job they've claimed as the household's consumer.",
    profiles: 28
  },
  {
    name: "Ritual Sippers",
    description: "Bone broth is their pause button. Stretched thin by caregiving, they sip it warm to feel briefly nourished and looked after.",
    profiles: 11
  },
  {
    name: "Body Optimizers",
    description: "Bone broth is fuel — protein grams, calorie ratio, macros that fit a daily target. Disciplined, goal-oriented, and pragmatic.",
    profiles: 17
  },
  {
    name: "Flavor Architects",
    description: "Bone broth has one job — make the dish taste like they spent all day on it. Competence-driven home cooks who want depth, richness, restaurant-quality results.",
    profiles: 20
  },
  {
    name: "Budget Pragmatists",
    description: "Bone broth is a cooking ingredient, not a wellness product. They want consistent, predictable meals at a price the budget can absorb.",
    profiles: 16
  }
];

// Archer Jerky segment options - Meat Snack Consumers
// Maps a population name to its available segments. Populations not listed
// here have no segment sub-slices, so the segment-selection step is skipped
// and the simulation runs against the whole population.
const SEGMENTS_BY_POPULATION: Record<string, SegmentOption[]> = {
  population_vitamin_survey_enriched_copy: VITAMIN_SEGMENT_OPTIONS,
  population_wp_dog_food: DOG_FOOD_SEGMENT_OPTIONS,
  population_wp_cat_food: CAT_FOOD_SEGMENT_OPTIONS,
  population_knf_bone_broth: KNF_SEGMENT_OPTIONS,
  // population_archer_jerky: ARCHER_JERKY_SEGMENT_OPTIONS,
};

// A selectable population option (name + size + human-readable label).
type PopulationOption = { name: string; agent_count: number; displayName?: string };

// Per-user-type population sets — non-admin users see only their own audiences.
//
// NAMES ONLY, deliberately: agent counts come from /vitamin/populations at runtime.
// They used to be hardcoded here and had already drifted against the API and against
// each other — population_wp_cat_food was listed as both 225 and 250 below, and
// archer_jerky as 168 when the API reports 303. The count also feeds the backend's
// completion-poll budget (max_polls = sample_size * 2), so an understated number can
// time out a long run. Declaring only *which* populations a client may use keeps that
// class of drift impossible: this list says WHICH, the API says HOW MANY.
type ClientPopulation = { name: string; displayName?: string };

const getSegmentOptionsForPopulation = (population: string): SegmentOption[] =>
  SEGMENTS_BY_POPULATION[population] || [];

// Population-aware fallback in the external-segments (VitaminSegment) shape, used
// when a population's /segments endpoint returns nothing. Preserves the original
// hardcoded audience defaults (vitamin, dog/cat food, KNF, Archer jerky) as a
// segment-level multi-select with no sub-segments.
const getFallbackSegments = (population: string): VitaminSegment[] =>
  getSegmentOptionsForPopulation(population).map((s, i) => ({
    segment_id: i + 1,
    segment_name: s.name,
    segment_description: s.description,
    agent_count: s.profiles,
    sub_segments: [],
  }));

/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

// Animated "…" shown while the matched-agent count is being recomputed.
const LoadingDots: React.FC = () => (
  <span className="vct-dots" aria-label="updating"><i /><i /><i /></span>
);


interface VitaminConceptTestFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void | Promise<void>;
  /**
   * Marketplace mode: the audience is already chosen upstream and the marketplace
   * has its own review/run step, so skip this form's internal audience (segments)
   * AND review steps — name → concept → submit. Defaults false (enterprise flow
   * is unchanged).
   */
  marketplaceMode?: boolean;
}

interface Concept {
  id: string;
  conceptStr: string;
  fileName: string | null;
  fileSize: number | null;
  brandName: string;
  imageDataUrl: string | null;
  parsing?: boolean;
}

const BRAND_NAME_RE = /Brand Name:\**\s*(.+?)(?:\n|$)/;

/** Clear any `parsing: true` — used on restore, where a mid-parse reload persisted
 *  the flag but no request is in flight, so a stuck flag would re-arm the nav gate. */
export function stripConceptParsing(concepts: Concept[]): Concept[] {
  return concepts.map((c) => (c.parsing ? { ...c, parsing: false } : c));
}

/** Apply a patch to the concept with `id`, re-deriving brandName from the text.
 *  Keyed by id (not index) so an async update whose card was removed mid-flight is
 *  dropped cleanly instead of writing to a shifted/phantom slot. */
export function patchConceptById(concepts: Concept[], id: string, patch: Partial<Concept>): Concept[] {
  const i = concepts.findIndex((c) => c.id === id);
  if (i === -1) return concepts;   // concept was removed — discard the stale update
  const next = [...concepts];
  const merged = { ...next[i], ...patch };
  const m = (merged.conceptStr || '').match(BRAND_NAME_RE);
  merged.brandName = m ? m[1].replace(/\*+/g, '').trim() : '';
  next[i] = merged;
  return next;
}

type Step = 'name' | 'concept' | 'population' | 'segments' | 'review';

/**
 * Which step a re-mounted form should open on.
 *
 * The form is unmounted whenever the user leaves it — in the marketplace, going forward to
 * Review and then Back re-mounts it. Answers survive via `initialFormData`; the step has to
 * be restored the same way, or the user lands on step 1 with every answer already filled in
 * and has to click forward through the wizard to reach the one they wanted to edit.
 *
 * Marketplace mode collapses the wizard to `name -> concept`: `hasPopulationStep` and
 * `hasSegments` are both forced false there and `concept` submits directly, so restoring any
 * later step would strand the user on one with no content and no way forward.
 */
export const resolveInitialStep = (saved: unknown, marketplaceMode: boolean): Step => {
  const valid: Step[] = ['name', 'concept', 'population', 'segments', 'review'];
  if (typeof saved !== 'string' || !valid.includes(saved as Step)) return 'name';
  const step = saved as Step;
  if (marketplaceMode) return step === 'concept' ? 'concept' : 'name';
  return step;
};

/* ═══════════════════════════════════════════════════════════════════════════
   HELPER COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

const QNumber: React.FC<{ index: number; total: number }> = ({ index, total }) => (
  <div className="vct-q-num">
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon.arrowRight style={{ width: 11, height: 11 }} />
      {index} <span style={{ color: 'var(--dim)', fontWeight: 400 }}>/ {total}</span>
    </span>
  </div>
);

const OkRow: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  label?: string;
  showHint?: boolean;
  /** Renders a Back button beside the primary action. Supplied on every step so the
   *  way backwards is always visible next to the way forwards — the floating chevrons
   *  are easy to miss, and on the first step they cannot go back at all. */
  onBack?: () => void;
  /** Disable the Back button too (e.g. while a concept image is still parsing, when
   *  navigating away would strand the upload). */
  backDisabled?: boolean;
  /** Replaces the "press Enter" hint when set (e.g. a "reading your concept…" status). */
  hint?: React.ReactNode;
}> = ({ onClick, disabled = false, label = "OK", showHint = true, onBack, backDisabled = false, hint }) => (
  <div className="vct-ok-row">
    {onBack && (
      <button type="button" className="vct-back-btn" onClick={onBack} disabled={backDisabled}>
        <Icon.arrowLeft />
        Back
      </button>
    )}
    <button className="vct-ok-btn" onClick={onClick} disabled={disabled}>
      {label}
      <Icon.check style={{ width: 14, height: 14 }} />
    </button>
    {hint ? (
      <span className="vct-enter-hint">{hint}</span>
    ) : showHint && (
      <span className="vct-enter-hint">
        press <span className="vct-kbd">Enter ↵</span>
      </span>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const VitaminConceptTestForm: React.FC<VitaminConceptTestFormProps> = ({
  onBack,
  onSavePayload,
  onFormDataChange,
  initialFormData = {},
  marketplaceMode = false,
}) => {
  // Inject styles
  useEffect(() => { injectVctStyles(); }, []);

  // Auth
  const { isAdmin } = useAuth();
  // The summit cohort skips this form's segment step and runs the whole population.
  //
  // Expressed as the PLAN FEATURE `autoSelectSingleSegment`, not an `isSummitUser` check — the same
  // decision `singleSegmentSkip.ts` documents: the next cohort with this need then requires no code
  // change, and there is no second hardcoded client list to drift out of step with the first.
  //
  // Broader than `decideSingleSegmentSkip`, deliberately. That one skips only a ONE-segment audience,
  // because clicking the single option is a dead click. Here the step is skipped at ANY segment count:
  // Puneet, on a summit audience with several segments — "no segments and run on full audience". The
  // step is suppressed, not silently narrowed, so `allAgents` below keeps the run at full population.
  const { autoSelectSingleSegment, clientConfig } = useSubscription();
  // Served (client_wiring -> concept-test/defaults), replacing an isBacchusUser ternary.
  const conceptDefaults = wiringValue<{ population?: string; sample_size?: number }>(
    clientConfig, 'concept-test', 'defaults', {},
  );
  // Served by /plans/current, which resolves them from this account's grants — the display name
  // is the grant's own label, so the same population reads "Dogs" for Wellness Pet and "Dog Food
  // Buyers" for Bacchus. null = no client configuration, which keeps the unrestricted behaviour.
  const clientPopulations = useMemo(
    () => clientConfig?.populations?.length ? clientConfig.populations : null,
    [clientConfig],
  );

  // State
  // Restored from the draft like every other field below. Without this, coming Back from
  // the marketplace Review screen re-mounted the form with all the answers intact but the
  // wizard rewound to step 1, so the user had to click forward through every step to reach
  // the one they actually wanted to edit.
  const [step, setStep] = useState<Step>(() => resolveInitialStep(initialFormData.step, marketplaceMode));
  const [conceptIndex, setConceptIndex] = useState(0);
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [count, setCount] = useState<string>(initialFormData.count || '1');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [concepts, setConcepts] = useState<Concept[]>(() => {
    // Strip any persisted `parsing: true` on restore: a mid-parse reload persisted
    // that flag through formState/sessionStorage, and no request is in flight after a
    // reload — a stuck `parsing` would re-arm the nav gate with nothing to clear it.
    if (initialFormData.concepts?.length) {
      return stripConceptParsing(initialFormData.concepts);
    }
    return [{ id: Math.random().toString(36).slice(2), conceptStr: '', fileName: null, fileSize: null, brandName: '', imageDataUrl: null }];
  });
  // Segment / sub-segment filtering against the external population.
  const [populationSegments, setPopulationSegments] = useState<VitaminSegment[]>([]);
  const [totalAgents, setTotalAgents] = useState<number | null>(null);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  // "All agents (no filter)" runs the full population; the default.
  const [allAgents, setAllAgents] = useState<boolean>(initialFormData.allAgents ?? true);
  // Dynamic mode: selected sub-segment names per segment name. Empty list for a
  // segment => that whole segment runs (all its sub-segments).
  const [subSelections, setSubSelections] = useState<Record<string, string[]>>(
    initialFormData.subSelections || {}
  );
  // Fallback / no-sub-segment mode: segment-level multi-select.
  const [selectedSegments, setSelectedSegments] = useState<string[]>(
    initialFormData.selectedSegments || []
  );
  // The summit cohort never sees the segment step, so any narrowing left in a restored draft would
  // apply invisibly — the run would quietly cover part of the population while the UI showed no
  // segment choice at all. Reset to the full population once the plan feature resolves (it starts
  // false and flips when /plans/current answers, so this cannot be an initialiser).
  useEffect(() => {
    if (!autoSelectSingleSegment) return;
    setAllAgents(true);
    setSelectedSegments([]);
    setSubSelections({});
    setClaritySelections({});
    // Also leave the step if we are somehow ON it. Gating the Review Edit button is the fix for the
    // reported route in; this is the backstop for every other one — a restored draft whose `step` was
    // 'segments', a stale URL, or a future caller of handleEdit. Reached that way the step also
    // rendered "0 / N" in the counter, because qIndexFor never matches a suppressed step.
    setStep(prev => (prev === 'segments' ? 'review' : prev));
  }, [autoSelectSingleSegment]);

  // Per-segment distinction-clarity selections: segment name -> selected clarity keys
  // (e.g. { "Sticks": ["clear"] }). Options + counts come from each segment's
  // distinction_clarity in the segments API. Empty for a segment => no clarity narrowing.
  const [claritySelections, setClaritySelections] = useState<Record<string, string[]>>(
    initialFormData.claritySelections || {}
  );

  // Admin options
  const [showAdmin, setShowAdmin] = useState(false);
  const [populations, setPopulations] = useState<PopulationOption[]>([]);
  const [populationsLoading, setPopulationsLoading] = useState(true);
  const [selectedPopulation, setSelectedPopulation] = useState(
    conceptDefaults.population ?? DEFAULT_CONCEPT_POPULATION
  );
  const [sampleSize, setSampleSize] = useState<number>(
    initialFormData.sampleSize ?? conceptDefaults.sample_size ?? 0,
  );
  // Sample size auto-matches the selected agent count until the admin overrides it. On an
  // Edit & Rerun the original sample size is restored from initialFormData and treated as an
  // override so auto-match doesn't clobber the reproduced run.
  const [sampleSizeTouched, setSampleSizeTouched] = useState(initialFormData.sampleSize != null);
  const [enableNorms, setEnableNorms] = useState(false);
  const [enableAgentSearch, setEnableAgentSearch] = useState(false);
  const [enableNovelReasoning, setEnableNovelReasoning] = useState(false);
  const [enableBrandPositioning, setEnableBrandPositioning] = useState(false);
  const [enableMultiModel, setEnableMultiModel] = useState(false);
  const [enableScoreGroupedFd, setEnableScoreGroupedFd] = useState(false);
  const [enableRadiusScoring, setEnableRadiusScoring] = useState(false);
  const [researchQuestion, setResearchQuestion] = useState('');

  // UI state
  const [showSample, setShowSample] = useState(false);
  const [drag, setDrag] = useState(false);
  const [parsing, _setParsing] = useState(false);
  const [uploadNotices, setUploadNotices] = useState<string[]>([]);
  // A failed launch used to be a console.error and nothing else, so clicking "Launch test" with the
  // plan's simulations spent did visibly nothing at all. The refusal names the limit; show it.
  const [submitError, setSubmitError] = useState<string>('');
  const [showIntroBanner, setShowIntroBanner] = useState(false);
  useEffect(() => {
    // No "trying our new UI" banner in the consumer marketplace flow.
    if (!marketplaceMode && localStorage.getItem('vitamin_concept_test_intro_seen') !== 'true') {
      setShowIntroBanner(true);
    }
  }, [marketplaceMode]);
  const dismissIntroBanner = () => {
    localStorage.setItem('vitamin_concept_test_intro_seen', 'true');
    setShowIntroBanner(false);
  };
  const fileRef = useRef<HTMLInputElement>(null);

  // Populations always come from /vitamin/populations — the single source of truth for
  // both names and agent counts. A client with a configured set sees only those, in the
  // configured order and with its own labels, but the COUNT is whatever the API reports;
  // admins see everything; anyone else gets the default vitamin population.
  useEffect(() => {
    // A registered client sees only its configured populations (empty list => treat as
    // no configured set, same as a non-client). Order/labels come from the registry.
    const allowed: ClientPopulation[] | null =
      clientPopulations && clientPopulations.length > 0 ? clientPopulations : null;

    // NO EARLY RETURN. There used to be one here — "non-admin with no configured set: single default
    // population, no fetch needed" — which set `population_vitamin_survey_enriched_copy` and returned. Its
    // premise held while every non-client account was internal, and `/vitamin/populations` being scoped per
    // organization is precisely what made it false: the backend now answers this question correctly and the
    // page has to ask. Every AI-summit attendee took that branch, which is how Harmless Harvest — granted
    // `population_natural_organic` — saw the vitamin population and "Running all 126 agents".
    //
    // `resolvePopulationSelection` below then corrects the selection, and it could never run from here.

    let cancelled = false;
    (async () => {
      let fetched: PopulationOption[] = [];
      try {
        const resp = await fetch(`${API_URL}/vitamin/populations`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          fetched = data.populations || data || [];
        }
      } catch (e) {
        console.error('Failed to fetch populations:', e);
      }
      if (cancelled) return;

      const byName = new Map(fetched.map(p => [p.name, p]));
      // Configured clients: keep their order/labels, take the count from the API. If the
      // fetch failed, still offer the names (count 0) rather than an empty picker.
      const resolved: PopulationOption[] = allowed
        ? allowed.map(({ name, displayName }) => {
            const live = byName.get(name);
            return { name, agent_count: live?.agent_count ?? 0, displayName: displayName ?? live?.displayName };
          })
        : fetched;

      setPopulations(resolved);
      if (resolved.length > 0) {
        // A configured client MUST land on its own first population, overriding the
        // 'population_vitamin_survey_enriched_copy' initial default. A single-population
        // client (e.g. Amy's) skips the population picker entirely, so if we don't set the
        // name here it stays on the vitamin default and segments/agents are fetched for the
        // WRONG population — the reported "Amy's sees SmartyPants populations" bug. Only the
        // admin path (allowed === null, full picker shown) keeps the user's manual choice.
        // Keyed on whether the current selection is AVAILABLE, not on whether we recognise the client.
        // The old `allowed ? ... : prev || ...` left every account without a clients.json entry — which is
        // every summit attendee — pinned to the hardcoded `population_vitamin_survey_enriched_copy` default,
        // because `prev` is always truthy. That is the tester's "Running all 126 agents" against four
        // vitamin/mom segments: 126 is that default's agent count. An admin's manual choice still survives
        // while it is valid. See populationSelection.ts.
        setSelectedPopulation((prev: string) => resolvePopulationSelection({ available: resolved, current: prev }));
        // Only seed the sample size from a real count — 0 means the fetch failed and we
        // must not clobber the existing default with a bogus size. Also skip when the
        // user already has an explicit size (sampleSizeTouched is seeded true from a
        // restored draft), so resuming a saved run keeps the size that was saved with it.
        if (resolved[0].agent_count > 0 && !sampleSizeTouched) setSampleSize(resolved[0].agent_count);
      }
      setPopulationsLoading(false);
    })();

    return () => { cancelled = true; };
  }, [isAdmin, clientPopulations]);

  // Fetch segments + sub-segments for the selected population. Falls back to the
  // hardcoded vitamin segments when the population exposes none.
  const prevPopulationRef = useRef<string | null>(null);
  // In-flight /parse-image aborts, so navigating Back mid-parse can cancel them —
  // Back is always allowed, but leaving must not strand a concept "uploading" forever
  // (the original reported bug). Each parse registers its controller here.
  const activeParsesRef = useRef<Set<AbortController>>(new Set());

  // Authoritative full-population size for the CURRENTLY selected population, straight from the
  // /vitamin/populations list (the same number the audience picker shows). This is the source of
  // truth for the count whenever the /segments endpoint doesn't provide one — a population like
  // gummy candy has NO hardcoded fallback in SEGMENTS_BY_POPULATION, so without this its segments
  // fetch failing would leave the PREVIOUS population's stale size (e.g. cat food's 250) in
  // sampleSize/totalAgents. It never falls back to the old hardcoded 126; a genuine lookup miss
  // surfaces as 0 (evidently wrong) instead of a plausible-but-wrong number.
  const selectedPopulationCount = useMemo(
    () => populations.find(p => p.name === selectedPopulation)?.agent_count ?? 0,
    [populations, selectedPopulation],
  );

  useEffect(() => {
    if (!selectedPopulation) return;
    // Wait until the populations effect has resolved the real selection. Without this the
    // segments effect fires once for the initial 'population_vitamin_survey_enriched_copy'
    // default (a wasted call for the WRONG population) and again once the client's real
    // population is set. Worse, that stray vitamin call returns total_agents:126 and can land
    // AFTER the populations effect set the client's real count (e.g. Amy's 194), racing it
    // back down to 126 in the review step. Gating on populationsLoading collapses it to a
    // single fetch for the correct population. (Admins never enter loading with a stale
    // default the same way; the picker drives selection.)
    if (populationsLoading) return;
    let cancelled = false;
    // Reset the selection only when the population actually CHANGES at runtime (e.g. the
    // admin picker), so stale segment names don't carry over. Skipped on the initial mount
    // so a rerun-restored selection (from initialFormData) survives.
    const isPopulationChange =
      prevPopulationRef.current !== null && prevPopulationRef.current !== selectedPopulation;
    if (isPopulationChange) {
      setAllAgents(true);
      setSubSelections({});
      setSelectedSegments([]);
      setClaritySelections({});
      // Re-enable auto-match for the new population so a stale manual sample size from the
      // previous population doesn't carry over.
      setSampleSizeTouched(false);
    }
    prevPopulationRef.current = selectedPopulation;
    setSegmentsLoading(true);
    // Population-aware hardcoded fallback (preserves per-population audience defaults). When a
    // population has no hardcoded segments (e.g. gummy candy), fall back to the live full-
    // population count so the total is never left stale from the previous population.
    const applyFallback = () => {
      const fallback = getFallbackSegments(selectedPopulation);
      setPopulationSegments(fallback);
      setTotalAgents(fallback.reduce((n, s) => n + s.agent_count, 0) || selectedPopulationCount || null);
    };
    // Reseed the sample size to the population's own count on a switch (or first load without an
    // explicit size), so a previous population's size never carries over. Prefer the /segments
    // total, then the live /populations count.
    const reseedSampleSize = (segTotal?: number) => {
      const n = segTotal || selectedPopulationCount;
      if (n > 0 && (isPopulationChange || !sampleSizeTouched)) setSampleSize(n);
    };
    (async () => {
      try {
        const data = await fetchPopulationSegments(selectedPopulation);
        if (cancelled) return;
        if (data.segments.length > 0) {
          setPopulationSegments(data.segments);
          setTotalAgents(data.total_agents || selectedPopulationCount || null);
        } else {
          applyFallback();
        }
        reseedSampleSize(data.total_agents);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to fetch population segments, using fallback:', e);
        applyFallback();
        // Even when the fetch fails, anchor the size to the live population count rather than
        // leaving the previous population's stale value in place.
        reseedSampleSize();
      } finally {
        if (!cancelled) setSegmentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPopulation, populationsLoading]);

  // Count is now derived from concepts (for backwards compatibility with form data)
  useEffect(() => {
    const validCount = concepts.filter(c => hasRealConceptContent(c.conceptStr)).length;
    if (validCount >= 5) setCount('5+');
    else if (validCount > 0) setCount(String(validCount));
    else setCount('1');
  }, [concepts]);

  // Update concept
  const updateConcept = useCallback((idx: number, patch: Partial<Concept>) => {
    setConcepts(prev => {
      const next = [...prev];
      const merged = { ...next[idx], ...patch };
      const m = (merged.conceptStr || '').match(/Brand Name:\**\s*(.+?)(?:\n|$)/);
      merged.brandName = m ? m[1].replace(/\*+/g, '').trim() : '';
      next[idx] = merged;
      return next;
    });
  }, []);

  // Update a concept by its stable id, not its index. Async work (image parse) must
  // use this: if the user removes an earlier card mid-parse, the surviving card
  // shifts index, so an index-keyed update would write to a phantom slot and leave
  // the real card stuck parsing forever. Keying by id drops the update cleanly when
  // the concept is gone.
  const updateConceptById = useCallback((id: string, patch: Partial<Concept>) => {
    setConcepts(prev => patchConceptById(prev, id, patch));
  }, []);

  // Marketplace resolves Twins from the pre-selected audience server-side, so the
  // form's own audience (segments) step is suppressed there. Outside marketplace
  // the step shows whenever the population exposes segments (real or fallback);
  // populations with none skip it and run the full population.
  const hasSegments =
    !marketplaceMode && !autoSelectSingleSegment && (segmentsLoading || populationSegments.length > 0);

  // Multi-population users (e.g. Bacchus, WellnessPet, demo) pick their audience on a
  // dedicated step before segments. Marketplace pre-selects the audience upstream, and
  // admins can still override via the admin panel.
  // The summit cohort ALWAYS sees the population step, even at one population. Puneet: "when ai
  // summit then always show population selection step, if 1 population or 2 show it, but only for
  // ai summit orgs". A single-population account otherwise jumped straight past it and never saw
  // WHICH panel it was about to run against — the same complaint that produced "Running all 126
  // agents" against a population nobody had chosen.
  //
  // Same `autoSelectSingleSegment` plan feature that suppresses the SEGMENT step. Opposite
  // direction on purpose: one option to click is a dead click for segments (nothing to decide) but
  // is information for populations (which panel am I testing?).
  const hasPopulationStep =
    !marketplaceMode && (populations.length > 1 || (autoSelectSingleSegment && populations.length > 0));

  // Whether the current population exposes sub-segments (drives narrowing UI).
  const hasSubSegments = useMemo(
    () => populationSegments.some(s => s.sub_segments.length > 0),
    [populationSegments]
  );

  // Per-segment distinction-clarity toggle (chips live inside each segment card).
  // Toggle a distinction-clarity chip for a segment. Independent of sub-segments —
  // selecting clarity alone (no sub-segment) is valid. Narrows the same way sub-segment
  // chips do (moves out of "all agents" into an explicit selection).
  const toggleClarity = (segmentName: string, key: string) => {
    if (allAgents) {
      setAllAgents(false);
      setSelectedSegments(populationSegments.map(s => s.segment_name));
    } else if (!selectedSegments.includes(segmentName)) {
      setSelectedSegments(prev => [...prev, segmentName]);
    }
    setClaritySelections(prev => {
      const cur = prev[segmentName] || [];
      const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
      const updated = { ...prev };
      if (next.length) updated[segmentName] = next;
      else delete updated[segmentName];
      return updated;
    });
  };

  // ── Unified checkbox selection ────────────────────────────────────────────
  // `allAgents` backs the top "Select all (entire population)" checkbox. When it
  // is off, `selectedSegments` holds the explicitly-checked segments and
  // `subSelections` narrows individual checked segments by sub-segment.
  const allChecked =
    allAgents ||
    (populationSegments.length > 0 && selectedSegments.length === populationSegments.length);

  const isSegmentChecked = (name: string) =>
    allAgents || selectedSegments.includes(name);

  // Top "Select all" checkbox: check or clear every segment at once.
  const toggleSelectAll = () => {
    setSubSelections({});
    setSelectedSegments([]);
    setAllAgents(!allChecked);
  };

  // Toggle a single segment's checkbox.
  const toggleSegment = (name: string) => {
    const allNames = populationSegments.map(s => s.segment_name);
    const checked = allAgents ? allNames : selectedSegments;
    const next = checked.includes(name)
      ? checked.filter(n => n !== name)
      : [...checked, name];
    // Drop any narrowing for a segment that just got unchecked.
    if (!next.includes(name)) {
      setSubSelections(prev => {
        if (!prev[name]) return prev;
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
    // Everything checked collapses back to the "all agents / no filter" state.
    if (next.length === allNames.length) {
      setAllAgents(true);
      setSelectedSegments([]);
    } else {
      setAllAgents(false);
      setSelectedSegments(next);
    }
  };

  // Narrow a checked segment by toggling one of its sub-segment chips. Narrowing
  // implies an explicit selection that still keeps every checked segment in.
  const toggleSubSegment = (segmentName: string, subName: string) => {
    if (allAgents) {
      // Move from "all agents" to an explicit list that still includes everyone.
      setAllAgents(false);
      setSelectedSegments(populationSegments.map(s => s.segment_name));
    } else if (!selectedSegments.includes(segmentName)) {
      setSelectedSegments(prev => [...prev, segmentName]);
    }
    setSubSelections(prev => {
      const current = prev[segmentName] || [];
      const next = current.includes(subName)
        ? current.filter(s => s !== subName)
        : [...current, subName];
      const updated = { ...prev };
      if (next.length) updated[segmentName] = next;
      else delete updated[segmentName];
      return updated;
    });
  };

  // Agents covered by the current selection (for the summary line).
  const selectedAgentCount = useMemo(() => {
    const checkedNames = allAgents
      ? populationSegments.map(s => s.segment_name)
      : selectedSegments;
    const count = populationSegments
      .filter(seg => checkedNames.includes(seg.segment_name))
      .reduce((sum, seg) => {
        const subs = subSelections[seg.segment_name] || [];
        if (!subs.length) return sum + seg.agent_count;
        return sum + seg.sub_segments
          .filter(ss => subs.includes(ss.sub_segment_name))
          .reduce((n, ss) => n + ss.agent_count, 0);
      }, 0);
    // "All agents" with no narrowing prefers the API's authoritative total.
    if (allAgents) return totalAgents ?? count;
    return count;
  }, [allAgents, totalAgents, populationSegments, subSelections, selectedSegments]);

  // The exact filter body sent to the run / filter-count API. `{}` => full population.
  const buildFilterBody = useCallback((): Record<string, unknown> => {
    if (allAgents || selectedSegments.length === 0) return {};
    if (hasSubSegments) {
      return {
        segment_filter: selectedSegments.map(name => {
          const subs = subSelections[name] || [];
          const clarity = claritySelections[name] || [];
          const entry: Record<string, unknown> = { segment: name };
          if (subs.length) entry.sub_segments = subs;
          if (clarity.length) entry.distinction_clarity = clarity;
          return entry;
        }),
      };
    }
    return { segments: selectedSegments };
  }, [allAgents, selectedSegments, subSelections, claritySelections, hasSubSegments]);

  // Live matched-agent count from the external filter-count API (accounts for
  // distinction-clarity + real overlaps, unlike the client-side estimate). Debounced
  // so rapid chip toggles don't spam the endpoint. No filter => the population total.
  const [matchedCount, setMatchedCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  // Per-segment matched counts under the current filter (from the API breakdown),
  // used to live-update the number shown on each segment card. Empty => cards show
  // their static totals.
  const [segmentBreakdown, setSegmentBreakdown] = useState<Record<string, number>>({});
  useEffect(() => {
    const body = buildFilterBody();
    if (!Object.keys(body).length) {
      setMatchedCount(totalAgents);
      setSegmentBreakdown({});
      setCountLoading(false);
      return;
    }
    if (!selectedPopulation) return;
    let cancelled = false;
    setCountLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetchFilterCount(selectedPopulation, body);
        if (cancelled) return;
        setMatchedCount(typeof res.matched_agents === 'number' ? res.matched_agents : null);
        const bd: Record<string, number> = {};
        (res.breakdown || []).forEach(b => {
          if (b && typeof b.matched === 'number') bd[b.segment] = b.matched;
        });
        setSegmentBreakdown(bd);
      } catch {
        if (!cancelled) { setMatchedCount(null); setSegmentBreakdown({}); } // fall back to estimates
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [buildFilterBody, selectedPopulation, totalAgents]);

  // Can the user continue from the segments step?
  const canProceedSegments = allAgents || selectedSegments.length > 0;

  // Upper bound for the sample size — you can't sample more agents than the current selection
  // contains. Replaces the old fixed 500 cap, which both blocked larger selections and was
  // bypassed by auto-match.
  const maxSample = selectedAgentCount || totalAgents || 500;

  // Auto-match the sample size to the current selection so every selected agent runs,
  // unless the admin has manually set a value.
  useEffect(() => {
    // Prefer the exact matched count from the API; fall back to the client estimate.
    const n = matchedCount ?? selectedAgentCount;
    if (!sampleSizeTouched && n > 0) {
      setSampleSize(n);
    }
  }, [matchedCount, selectedAgentCount, sampleSizeTouched]);

  // Human-readable summary of the current selection (for the review screen).
  // The population's human name, falling back to the raw id with its `population_` prefix stripped
  // so an unlabelled one still reads as words rather than a slug.
  const populationLabel = useMemo<string>(() => {
    const p = populations.find(x => x.name === selectedPopulation);
    if (p?.displayName) return p.displayName;
    const raw = p?.name || selectedPopulation || '';
    return raw.replace(/^population_/, '').replace(/_/g, ' ').trim();
  }, [populations, selectedPopulation]);

  // Review names the PANEL, not just the breadth. "All agents (full population)" told the author
  // how much of something they were running against but never WHICH something, so two accounts on
  // different panels read identically on the last screen before launch.
  const selectionSummary = useMemo<string[]>(() => {
    if (allAgents) {
      const count = selectedAgentCount || matchedCount;
      const breadth = count ? `all ${count} agents` : 'all agents';
      return [populationLabel ? `${populationLabel} — ${breadth}` : `All agents (full population)`];
    }
    return selectedSegments.map(name => {
      const subs = subSelections[name] || [];
      return subs.length ? `${name}: ${subs.join(', ')}` : name;
    });
  }, [allAgents, subSelections, selectedSegments, populationLabel, selectedAgentCount, matchedCount]);

  // Notify form data change
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        simName, count, concepts, selectedPopulation, sampleSize,
        allAgents, subSelections, selectedSegments, claritySelections,
        // Persisted so Back from Review returns to the step the user left, not step 1.
        step,
      });
    }
  }, [simName, count, concepts, selectedPopulation, sampleSize, allAgents, subSelections, selectedSegments, claritySelections, step]);

  // Progress calculation - name + concept + (segments) + review.
  // The segments step is omitted when the population has no segment slices.
  // Base non-marketplace flow = name + concept + review (3), plus optional population
  // and segments steps. Marketplace collapses to name + concept.
  const totalQs = marketplaceMode ? 2 : (3 + (hasPopulationStep ? 1 : 0) + (hasSegments ? 1 : 0));

  // Flow: name -> concept -> population (if multiple) -> segments (if any) -> review
  const qIndexFor = (s: Step) => {
    let idx = 1;
    if (s === 'name') return idx;
    idx++;
    if (s === 'concept') return idx;
    if (hasPopulationStep) { idx++; if (s === 'population') return idx; }
    if (hasSegments) { idx++; if (s === 'segments') return idx; }
    idx++;
    if (s === 'review') return idx;
    return 0;
  };

  const progressPct = useMemo(() => {
    if (step === 'review') return 100;
    return (qIndexFor(step) / totalQs) * 100;
  }, [step, totalQs, hasPopulationStep, hasSegments]);

  // Navigation
  // A concept image is still being read/parsed into text. Navigating away now either ships
  // an unparsed concept (Continue) or strands the upload spinner (Back), so all navigation
  // is blocked until every concept finishes — the buttons and chevrons below disable, and
  // these guards also cover the Enter-key path.
  const anyParsing = parsing || concepts.some(c => c.parsing);

  const goNext = () => {
    if (anyParsing) return;
    if (step === 'name') { setStep('concept'); return; }
    if (step === 'concept') {
      // Marketplace: audience already chosen + its own review step → submit now.
      if (marketplaceMode) { handleSubmit(); return; }
      setStep(hasPopulationStep ? 'population' : hasSegments ? 'segments' : 'review'); return;
    }
    if (step === 'population') { setStep(hasSegments ? 'segments' : 'review'); return; }
    if (step === 'segments') { setStep('review'); return; }
    if (step === 'review') { handleSubmit(); return; }
  };

  const goPrev = () => {
    // Back is the only exit from the concept step, so it must ALWAYS work — never gate
    // it on anyParsing, or a stuck parse traps the user with no escape but a reload.
    // (goNext stays gated; you can retreat but not advance mid-parse.) But leaving must
    // CANCEL any in-flight parse — otherwise backing out mid-upload strands the concept
    // "uploading" forever (the original reported bug). Aborting settles each parse via
    // its catch (fallback + parsing:false), so nothing is left stuck.
    if (activeParsesRef.current.size) {
      activeParsesRef.current.forEach((c) => c.abort());
      activeParsesRef.current.clear();
    }
    if (step === 'name') { onBack(); return; }
    if (step === 'concept') { setStep('name'); return; }
    if (step === 'population') { setStep('concept'); return; }
    if (step === 'segments') { setStep(hasPopulationStep ? 'population' : 'concept'); return; }
    if (step === 'review') { setStep(hasSegments ? 'segments' : hasPopulationStep ? 'population' : 'concept'); return; }
  };

  const canGoNext = useMemo(() => {
    if (anyParsing) return false;
    // At least one concept must have content
    if (step === 'concept') return concepts.some(c => hasRealConceptContent(c.conceptStr));
    if (step === 'population') return !!selectedPopulation;
    if (step === 'segments') return canProceedSegments;
    if (step === 'review') return !!simName.trim();
    return true;
  }, [step, concepts, canProceedSegments, simName, selectedPopulation, anyParsing]);

  // The chevrons move between QUESTIONS, so up stays disabled on the first step (there is
  // no earlier question). Leaving the flow entirely is a different action with its own
  // labelled control — the "Back" button rendered inline with OK (see OkRow), which calls
  // goPrev() on every step and so exits the flow from the first one.
  // Back is always available (retreat must never be blocked, even mid-parse).
  const canGoPrev = step !== 'name';

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = ((e.target as Element)?.tagName || '').toLowerCase();
      const isEditing = tag === 'input' || tag === 'textarea';

      if (e.key === 'Enter' && !e.shiftKey) {
        if (isEditing && tag === 'textarea') return;
        if (canGoNext) { e.preventDefault(); goNext(); }
      }
      if (e.shiftKey && e.key === 'Tab' && canGoPrev) {
        e.preventDefault();
        goPrev();
      }
      // Segment keyboard shortcuts (A-F): toggle a segment's checkbox.
      if (step === 'segments' && !isEditing && /^[a-f]$/i.test(e.key)) {
        e.preventDefault();
        const idx = e.key.toLowerCase().charCodeAt(0) - 97;
        if (idx < populationSegments.length) toggleSegment(populationSegments[idx].segment_name);
      }
      // Population keyboard shortcuts (A-Z): pick a population.
      if (step === 'population' && !isEditing && /^[a-z]$/i.test(e.key)) {
        const idx = e.key.toLowerCase().charCodeAt(0) - 97;
        if (idx < populations.length) {
          e.preventDefault();
          setSelectedPopulation(populations[idx].name);
          setSampleSize(populations[idx].agent_count);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, canGoNext, canGoPrev, allAgents, populationSegments, selectedSegments, populations]);

  // Open manual editor — creates an EMPTY concept and opens the modal. The template
  // is shown in the modal as a starting point but isn't committed until the user
  // edits it and saves, so the "saved" checkmark only appears after a real edit.
  const openManualEditor = () => {
    const newId = Math.random().toString(36).slice(2);
    const newConcept = {
      id: newId,
      conceptStr: '',
      fileName: null,
      fileSize: null,
      brandName: '',
      imageDataUrl: null
    };
    setConcepts(prev => {
      // If there's only one empty concept, replace it; otherwise add new one
      if (prev.length === 1 && !prev[0].conceptStr.trim() && !prev[0].fileName) {
        return [newConcept];
      }
      return [...prev, newConcept];
    });
    setConceptIndex(concepts.length === 1 && !concepts[0].conceptStr.trim() && !concepts[0].fileName ? 0 : concepts.length);
    setShowManualEntry(true);
  };

  // Remove a concept by index
  const removeConcept = (index: number) => {
    setConcepts(prev => prev.filter((_, i) => i !== index));
  };

  // Handle multiple files upload
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // New batch — clear notices from the previous one; each rejected file
    // appends its own message so none are lost in multi-file drops
    setUploadNotices([]);

    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Find an empty concept slot or create a new one
      const emptyIndex = concepts.findIndex(c => !c.conceptStr.trim() && !c.fileName);
      if (emptyIndex >= 0) {
        await handleFile(file, emptyIndex);
      } else {
        // Create a new concept and process the file into it
        const newId = Math.random().toString(36).slice(2);
        const newIndex = concepts.length;
        setConcepts(prev => [...prev, {
          id: newId,
          conceptStr: '',
          fileName: null,
          fileSize: null,
          brandName: '',
          imageDataUrl: null
        }]);
        // Small delay to ensure state updates
        await new Promise(r => setTimeout(r, 50));
        // Pass newId explicitly: `concepts[newIndex]` isn't in handleFile's closure yet.
        await handleFile(file, newIndex, newId);
      }
    }
  };

  // File handling - accepts optional targetIndex and an explicit concept id.
  const handleFile = async (file: File | undefined, targetIndex?: number, explicitConceptId?: string) => {
    if (!file) return;
    const idx = targetIndex ?? conceptIndex;
    // Bind the async parse to the concept's stable id, not its index — the user may
    // remove another card while this one parses (see updateConceptById). When the caller
    // just appended a brand-new concept, `concepts` in this closure is the PRE-append
    // render and won't contain it, so `concepts[idx]?.id` is undefined — the caller must
    // pass the new id explicitly (handleFiles does), or the upload silently no-ops.
    const conceptId = explicitConceptId ?? concepts[idx]?.id;
    if (!conceptId) return;

    // Validate file size
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      setUploadNotices(prev => [...prev, `"${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is ${MAX_UPLOAD_SIZE_MB}MB.`]);
      return;
    }

    // Validate file type via the shared gate — mime-first, so a WebP/HEIC renamed
    // .png is rejected here rather than 400'd by /parse-image (size already checked).
    const typeRejection = imageRejectionReason(file);
    if (typeRejection) {
      setUploadNotices(prev => [...prev, `"${file.name}" — ${typeRejection}`]);
      return;
    }

    // Immediately read image and show placeholder with loading state
    const readImageAsDataUrl = (): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    try {
      // First, read the image and show it immediately with parsing state
      const imageDataUrl = await readImageAsDataUrl();
      updateConceptById(conceptId, {
        fileName: file.name,
        fileSize: file.size,
        imageDataUrl,
        parsing: true,
      });
    } catch {
      // If image read fails, still show placeholder
      updateConceptById(conceptId, {
        fileName: file.name,
        fileSize: file.size,
        parsing: true,
      });
    }

    // A hung request (slow model, dropped wifi, laptop sleep) would otherwise never
    // settle, leaving parsing:true forever and — since the gate also blocks nav — a
    // paying user stuck. Abort after a ceiling so it falls through to the catch below,
    // which clears parsing via the fallback auto-fill.
    const controller = new AbortController();
    activeParsesRef.current.add(controller);
    const parseTimeout = setTimeout(() => controller.abort(), CONCEPT_PARSE_TIMEOUT_MS);
    try {
      // Field schema required by the API
      const fieldSchema = {
        fields: [
          { name: 'productHeadline', type: 'str', description: 'Product name/headline (main title)', required: true },
          { name: 'brandName', type: 'str', description: 'Brand/manufacturer name', required: true },
          { name: 'consumerInsight', type: 'str', description: 'One line Description - problem this solves', required: false },
          { name: 'mainBenefit', type: 'str', description: 'Detailed Description - main value proposition', required: true },
          { name: 'reasonsToBelieve', type: 'list[str]', description: 'Reasons to believe - key claims', required: false },
          { name: 'pricePoint', type: 'str', description: 'Price & Pack info', required: false },
          { name: 'packSize', type: 'str', description: 'Pack size/quantity', required: false },
        ],
        context: 'consumer product concept test'
      };

      const formData = new FormData();
      formData.append('image', file);
      formData.append('field_schema', JSON.stringify(fieldSchema));

      const response = await fetch(`${API_URL}/parse-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to parse image');
      }

      const data = await response.json();

      // Format parsed data to text (matching original format)
      const parts: string[] = [];
      if (data.productHeadline) parts.push(`**Product Headline:** ${data.productHeadline}`);
      if (data.brandName) parts.push(`**Brand Name:** ${data.brandName}`);
      if (data.consumerInsight) parts.push(`**One Line Description:** ${data.consumerInsight}`);
      if (data.mainBenefit) parts.push(`**Detailed Description:** ${data.mainBenefit}`);

      const rtbs = data.reasonsToBelieve?.filter(Boolean) || [];
      if (rtbs.length > 0) {
        parts.push(`**Reasons to Believe:**\n${rtbs.map((r: string) => `- ${r}`).join('\n')}`);
      }

      const pricePack = [data.pricePoint, data.packSize].filter(Boolean).join(' for ');
      if (pricePack) parts.push(`**Price & Pack:** ${pricePack}`);

      const conceptStr = parts.join('\n\n');

      // Update with parsed data (imageDataUrl already set from initial read)
      updateConceptById(conceptId, {
        conceptStr,
        brandName: data.brandName || '',
        parsing: false,
      });
    } catch (err) {
      console.error('Image parsing failed:', err);
      // Fallback auto-fill (imageDataUrl already set from initial read)
      const auto = `**Product Headline:** ${file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')}
**Brand Name:** Imported from upload
**One Line Description:** Auto-extracted from your concept card. Edit anything below.
**Detailed Description:** We pulled this from "${file.name}". Replace this paragraph with your full benefit story.

**Reasons to Believe:**
- Add your first claim
- Add your second claim
- Add your third claim

**Price & Pack:** $0.00 for 0 units`;

      updateConceptById(conceptId, { conceptStr: auto, parsing: false });
    } finally {
      clearTimeout(parseTimeout);
      activeParsesRef.current.delete(controller);
    }
  };

  // Submit
  const handleSubmit = async () => {
    // Build payload - must match the API format expected by /simulations endpoint
    const basePayload = {
      task: 'vitamin-concept-test',
      name: simName || 'Concept Test',
      population: selectedPopulation,
      // Never submit more agents than the current selection actually contains: clamp to the
      // matched/selected count (or the live full-population size) so a stale or oversized
      // sampleSize can't leak into the run. Mirrors the review-screen totalProfiles.
      sample_size: (() => {
        const cap = selectedAgentCount || selectedPopulationCount || 0;
        return cap ? Math.min(sampleSize || cap, cap) : (sampleSize || 0);
      })(),
      enable_norms: enableNorms,
    };

    let payload: Record<string, any>;

    // Filter out empty / unedited-template concepts
    const validConcepts = concepts.filter(c => hasRealConceptContent(c.conceptStr));
    const mode = validConcepts.length === 1 ? 'single' : 'multi';

    if (mode === 'single') {
      // Single concept mode - use concept_str directly
      payload = {
        ...basePayload,
        concept_str: validConcepts[0].conceptStr,
      };
    } else {
      // Multi-concept mode - use concepts array
      payload = {
        ...basePayload,
        concepts: validConcepts.map(c => ({
          concept_str: c.conceptStr,
        })),
      };
    }

    // Dog/cat-food single-concept tests always run with norms, regardless of the
    // admin toggle (restored behavior for WellnessPet/Bacchus non-admin users).
    if (mode === 'single' && (selectedPopulation === 'population_wp_dog_food' || selectedPopulation === 'population_wp_cat_food')) {
      payload.enable_norms = true;
    }

    // Apply segment filtering. "All agents" => no filter (full population).
    Object.assign(payload, buildFilterBody());


    // Add admin flags if enabled
    if (enableAgentSearch) {
      payload.enable_agent_search = true;
    }
    if (enableNovelReasoning) {
      payload.enable_novel_reasoning = true;
    }
    if (enableBrandPositioning) {
      payload.enable_brand_positioning = true;
    }
    if (IS_DEV_ENV && enableMultiModel) {
      payload.enable_multi_model = true;
    }
    if (IS_DEV_ENV && enableScoreGroupedFd) {
      payload.enable_score_grouped_fd = true;
    }
    if (IS_DEV_ENV && enableRadiusScoring) {
      payload.enable_radius_scoring = true;
    }
    if (researchQuestion.trim()) {
      payload.research_question = researchQuestion.trim();
    }

    try {
      setSubmitError('');
      if (onSavePayload) {
        await onSavePayload(payload);
      }
    } catch (e: unknown) {
      // `createSimulation` throws UsageLimitExceededError with the server's own sentence for a spent quota, and
      // a plain Error otherwise. Either way the message is the useful thing — swallowing it into the console is
      // what made a refused launch look like a dead button.
      console.error('Submit error:', e);
      const message = e instanceof Error ? e.message.trim() : '';
      setSubmitError(message || 'Could not start the test. Please try again.');
    }
  };

  // Agents in the current selection (shown on the review screen).
  // What actually runs: the external API caps the sample to the filtered set size.
  const totalProfiles = selectedAgentCount
    ? Math.min(sampleSize || selectedAgentCount, selectedAgentCount)
    : (sampleSize || totalAgents || selectedPopulationCount || 0);

  const handleEdit = (target: string) => {
    if (target === 'name') setStep('name');
    else if (target === 'concept') setStep('concept');
    else if (target === 'population') setStep('population');
    else if (target === 'segments') setStep('segments');
  };

  const qIdx = qIndexFor(step);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════════ */

  return (
    <div className={`vct-app ${marketplaceMode ? 'vct-app--mkt' : ''} ${showIntroBanner ? 'vct-app--banner' : ''}`}>
      {/* Progress bar */}
      <div className="vct-progress">
        <div className="vct-progress__fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Experimental UI intro banner — shown once per user */}
      {showIntroBanner && (
        <div className="vct-banner-wrap">
          <div className="vct-banner vct-banner-enter">
            <div className="vct-banner__icon"><Icon.sparkles /></div>
            <div className="vct-banner__body">
              <strong>You're trying our new concept-test UI.</strong>
              <span> It looks and feels different on purpose — share feedback if anything's off.</span>
            </div>
            <button className="vct-banner__cta" onClick={dismissIntroBanner}>Got it</button>
            <button className="vct-banner__close" onClick={dismissIntroBanner} aria-label="Dismiss">
              <Icon.x />
            </button>
          </div>
        </div>
      )}

      {/* ═══ NAME SCREEN ═══ */}
      {step === 'name' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">What should we call this <em>{marketplaceMode ? 'study?' : 'simulation?'}</em></h1>
            <p className="vct-q-help">A short label so you can find it later — feel free to skip and we'll name it for you.</p>
            <input
              className="vct-big-input"
              value={simName}
              placeholder={STUDY_NAME_EXAMPLE_PLACEHOLDER}
              onChange={e => setSimName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') goNext(); }}
              autoFocus
            />
            <OkRow onClick={goNext} onBack={goPrev} />
          </div>
        </div>
      )}

      {/* ═══ CONCEPT SCREEN ═══ */}
      {step === 'concept' && (() => {
        const hasAny = concepts.some(c => c.fileName || c.conceptStr.trim());
        const validCount = concepts.filter(c => hasRealConceptContent(c.conceptStr)).length;

        const getCardLabel = (c: typeof concepts[0]) => {
          if (c.brandName) return c.brandName;
          if (c.fileName) return c.fileName;
          if (c.conceptStr) {
            const h = c.conceptStr.match(/Product Headline:\**\s*(.+?)(?:\n|$)/);
            if (h) return h[1].replace(/\*+/g, '').trim();
          }
          return 'Untitled';
        };

        return (
          <div className="vct-screen vct-screen-enter">
            <div className="vct-screen__inner">
              <QNumber index={qIdx} total={totalQs} />
              <h1 className="vct-q-title">Upload your <em>concepts.</em></h1>
              <p className="vct-q-help">
                {hasAny
                  ? `${validCount} concept${validCount === 1 ? '' : 's'} ready. Add more, edit, or continue.`
                  : "Drop in one or more concept cards — we'll extract everything automatically."}
              </p>

              {/* EMPTY STATE */}
              {!hasAny && (
                <>
                  {/* Big Dropzone */}
                  <div
                    className={`vct-dropzone ${drag ? 'vct-dropzone--drag' : ''}`}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDrag(false);
                      handleFiles(e.dataTransfer.files);
                    }}
                    style={{ padding: '44px 28px' }}
                  >
                    <div className="vct-dropzone__icon">
                      {parsing
                        ? <div className="vct-spinner" style={{ width: 22, height: 22, borderWidth: 2 }} />
                        : <Icon.upload style={{ width: 20, height: 20 }} />}
                    </div>
                    <div className="vct-dropzone__title">
                      {parsing ? 'Reading your concept cards…' : <>Drop one or more concept cards or <a>browse</a></>}
                    </div>
                    <div className="vct-dropzone__hint">{UPLOAD_HINT_TEXT}</div>
                    <input
                      ref={fileRef}
                      type="file"
                      hidden
                      multiple
                      accept={ACCEPTED_IMAGE_INPUT}
                      onChange={e => { handleFiles(e.target.files); if (e.target) e.target.value = ''; }}
                    />
                  </div>

                  {uploadNotices.length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--error)', textAlign: 'center' }}>
                      {uploadNotices.map((notice, i) => <div key={i}>{notice}</div>)}
                    </div>
                  )}

                  {/* Requirements line */}
                  <div className="vct-req-line">
                    <span>Each card should include</span>
                    <strong>product headline</strong><span className="vct-req-line__sep" />
                    <strong>brand</strong><span className="vct-req-line__sep" />
                    <strong>description</strong><span className="vct-req-line__sep" />
                    <strong>claims</strong><span className="vct-req-line__sep" />
                    <strong>price & pack</strong>
                    <button type="button" className="vct-see-sample" onClick={() => setShowSample(true)}>
                      See sample
                    </button>
                  </div>

                  {/* OR divider */}
                  <div className="vct-or-divider">or</div>

                  {/* Write manually button */}
                  <button className="vct-secondary-btn" onClick={openManualEditor}>
                    <span className="vct-secondary-btn__icon"><Icon.fileText /></span>
                    <span className="vct-secondary-btn__body">
                      <div className="vct-secondary-btn__title">Write a concept manually</div>
                      <div className="vct-secondary-btn__sub">Use our guided template — fill in brand, claims, price & pack.</div>
                    </span>
                    <span className="vct-secondary-btn__arrow"><Icon.arrowRight /></span>
                  </button>
                </>
              )}

              {/* FILLED STATE */}
              {hasAny && (
                <>
                  {/* Concept Grid */}
                  <div className="vct-concept-grid">
                    {concepts.map((c, i) => {
                      if (!c.fileName && !c.conceptStr.trim()) return null;
                      const isWritten = !c.imageDataUrl && c.conceptStr.trim();
                      return (
                        <div
                          key={c.id}
                          className={`vct-concept-card ${c.parsing ? 'vct-concept-card--parsing' : ''}`}
                          onClick={() => {
                            setConceptIndex(i);
                            setShowManualEntry(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={`vct-concept-card__thumb ${isWritten ? 'vct-concept-card__thumb--text' : ''}`}>
                            {c.imageDataUrl ? (
                              <img src={c.imageDataUrl} alt={c.brandName || `Concept ${i + 1}`} />
                            ) : (
                              <Icon.fileText style={{ width: 32, height: 32, opacity: 0.7 }} />
                            )}
                            <span className="vct-concept-card__source">
                              {c.imageDataUrl ? <><Icon.image style={{ width: 11, height: 11 }} /> Image</> : <><Icon.fileText style={{ width: 11, height: 11 }} /> Written</>}
                            </span>
                            <button
                              className="vct-concept-card__remove"
                              onClick={(e) => { e.stopPropagation(); removeConcept(i); }}
                              title="Remove"
                            >
                              <Icon.x />
                            </button>
                            {/* Done checkmark — appears when parsing finishes */}
                            {!c.parsing && c.conceptStr.trim() && (
                              <div className="vct-concept-card__done" aria-label="Concept ready">
                                <Icon.check />
                              </div>
                            )}
                            {/* Hover hint overlay */}
                            <div className="vct-concept-card__hover-hint">
                              <Icon.edit />
                              <span>Click to edit</span>
                            </div>
                          </div>
                          <div className="vct-concept-card__meta">
                            <div className="vct-concept-card__num">Concept {i + 1}</div>
                            <div className="vct-concept-card__name">{getCardLabel(c)}</div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Tile */}
                    <button
                      className={`vct-add-tile ${drag ? 'vct-dropzone--drag' : ''}`}
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDrag(false);
                        handleFiles(e.dataTransfer.files);
                      }}
                    >
                      <span className="vct-add-tile__plus">
                        <Icon.upload style={{ width: 18, height: 18 }} />
                      </span>
                      <span>Upload more</span>
                      <span style={{ fontSize: 11, color: 'var(--dim)' }}>or drop here</span>
                      <input
                        ref={fileRef}
                        type="file"
                        hidden
                        multiple
                        accept={ACCEPTED_IMAGE_INPUT}
                        onChange={e => { handleFiles(e.target.files); if (e.target) e.target.value = ''; }}
                      />
                    </button>
                  </div>

                  {uploadNotices.length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--error)' }}>
                      {uploadNotices.map((notice, i) => <div key={i}>{notice}</div>)}
                    </div>
                  )}

                  {/* Requirements line */}
                  <div className="vct-req-line" style={{ marginTop: 14 }}>
                    <span>Each card should include</span>
                    <strong>product headline</strong><span className="vct-req-line__sep" />
                    <strong>brand</strong><span className="vct-req-line__sep" />
                    <strong>description</strong><span className="vct-req-line__sep" />
                    <strong>claims</strong><span className="vct-req-line__sep" />
                    <strong>price & pack</strong>
                    <button type="button" className="vct-see-sample" onClick={() => setShowSample(true)}>
                      See sample
                    </button>
                  </div>

                  {/* OR divider */}
                  <div className="vct-or-divider">or</div>

                  {/* Write manually button */}
                  <button className="vct-secondary-btn" onClick={openManualEditor}>
                    <span className="vct-secondary-btn__icon"><Icon.fileText /></span>
                    <span className="vct-secondary-btn__body">
                      <div className="vct-secondary-btn__title">Write a concept manually</div>
                      <div className="vct-secondary-btn__sub">Use our guided template — fill in brand, claims, price & pack.</div>
                    </span>
                    <span className="vct-secondary-btn__arrow"><Icon.arrowRight /></span>
                  </button>
                </>
              )}

              {/* Admin panel */}
              {isAdmin && (
                <div className="vct-admin-panel" style={{ marginTop: 20 }}>
                  <button className="vct-admin-panel__toggle" onClick={() => setShowAdmin(!showAdmin)}>
                    <Icon.settings style={{ width: 14, height: 14 }} />
                    Admin Options
                    <span style={{ marginLeft: 'auto' }}>{showAdmin ? '−' : '+'}</span>
                  </button>
                  {showAdmin && (
                    <div className="vct-admin-panel__content">
                      <div className="vct-admin-field">
                        <label>Population</label>
                        {populationsLoading ? (
                          <div style={{ fontSize: 14, color: 'var(--muted)', padding: '8px 0' }}>Loading populations...</div>
                        ) : populations.length > 0 ? (
                          <select
                            value={selectedPopulation}
                            onChange={e => setSelectedPopulation(e.target.value)}
                          >
                            {populations.map(p => (
                              // `title` keeps the raw key reachable: this is the admin panel, and the key is
                              // what an admin needs when correlating with a grant or the external API.
                              <option key={p.name} value={p.name} title={p.name}>
                                {populationLabelFor(p)} ({p.agent_count} agents)
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={selectedPopulation}
                            onChange={e => setSelectedPopulation(e.target.value)}
                            placeholder="e.g., population_vitamins"
                          />
                        )}
                      </div>
                      <div className="vct-admin-field">
                        <label>Sample Size <span style={{ fontSize: 11, color: 'var(--muted)' }}>(auto-matches selection)</span></label>
                        <input
                          type="number"
                          value={sampleSize}
                          onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            if (Number.isNaN(v)) return; // ignore empty/partial input instead of locking to a default
                            setSampleSize(Math.min(Math.max(v, 1), maxSample));
                            setSampleSizeTouched(true);
                          }}
                          min={1}
                          max={maxSample}
                        />
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Norms</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableNorms}
                            onChange={e => setEnableNorms(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Calculate norms on the fly</span>
                        </label>
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Agent Search</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableAgentSearch}
                            onChange={e => setEnableAgentSearch(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Enable agent search capability</span>
                        </label>
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Novel Reasoning</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableNovelReasoning}
                            onChange={e => setEnableNovelReasoning(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Enable novel reasoning mode</span>
                        </label>
                      </div>
                      <div className="vct-admin-field">
                        <label>Enable Brand Positioning</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableBrandPositioning}
                            onChange={e => setEnableBrandPositioning(e.target.checked)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14 }}>Enable brand positioning</span>
                        </label>
                      </div>
                      {IS_DEV_ENV && (
                        <div className="vct-admin-field">
                          <label>Enable Multi-Model <span style={{ fontSize: 11, color: 'var(--muted)' }}>(dev only)</span></label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={enableMultiModel}
                              onChange={e => setEnableMultiModel(e.target.checked)}
                              style={{ width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 14 }}>Run the concept test across multiple models</span>
                          </label>
                        </div>
                      )}
                      {IS_DEV_ENV && (
                        <div className="vct-admin-field">
                          <label>Enable Score Grouped FD <span style={{ fontSize: 11, color: 'var(--muted)' }}>(dev only)</span></label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={enableScoreGroupedFd}
                              onChange={e => setEnableScoreGroupedFd(e.target.checked)}
                              style={{ width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 14 }}>Enable grouped score FD</span>
                          </label>
                        </div>
                      )}
                      {IS_DEV_ENV && (
                        <div className="vct-admin-field">
                          <label>Source attribution <span style={{ fontSize: 11, color: 'var(--muted)' }}>(dev only)</span></label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={enableRadiusScoring}
                              onChange={e => setEnableRadiusScoring(e.target.checked)}
                              style={{ width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 14 }}>Trace every generated sentence back to its source</span>
                          </label>
                        </div>
                      )}
                      <div className="vct-admin-field">
                        <label>Research Question</label>
                        <input
                          type="text"
                          value={researchQuestion}
                          onChange={e => setResearchQuestion(e.target.value)}
                          placeholder="Optional research question..."
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <OkRow
                onClick={goNext}
                onBack={goPrev}
                disabled={!canGoNext}
                backDisabled={false}
                hint={anyParsing ? 'Reading your concept — one moment…' : undefined}
                label="Continue"
              />
            </div>
          </div>
        );
      })()}

      {/* ═══ POPULATION SCREEN ═══ */}
      {step === 'population' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">Select your <em>audience</em></h1>
            <p className="vct-q-help">Choose the consumer population for your concept test.</p>

            <div className="vct-choices">
              {populations.map((pop, i) => {
                const isSel = selectedPopulation === pop.name;
                return (
                  <button
                    key={pop.name}
                    className={`vct-choice ${isSel ? 'vct-choice--selected' : ''}`}
                    onClick={() => {
                      setSelectedPopulation(pop.name);
                      setSampleSize(pop.agent_count);
                    }}
                  >
                    <span className="vct-choice__key">{String.fromCharCode(65 + i)}</span>
                    <div style={{ flex: 1 }}>
                      <div className="vct-choice__label">{populationLabelFor(pop)}</div>
                    </div>
                    <span className="vct-choice__meta">{pop.agent_count} profiles</span>
                    <Icon.check className="vct-choice__check" />
                  </button>
                );
              })}
            </div>

            <OkRow onClick={goNext} onBack={goPrev} disabled={!selectedPopulation} />
          </div>
        </div>
      )}

      {/* ═══ SEGMENTS SCREEN ═══ */}
      {step === 'segments' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <div className="vct-seg-header">
              <h1 className="vct-q-title">Who should we <em>ask?</em></h1>
              {!segmentsLoading && populationSegments.length > 0 && (
                <button type="button" className="vct-seg-selectall" onClick={toggleSelectAll}>
                  {allChecked ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            <p className="vct-q-help">
              Everyone's included by default. Uncheck segments to narrow your audience
              {hasSubSegments ? ', or pick sub-segments to focus within a segment.' : '.'}
            </p>

            {segmentsLoading ? (
              <div className="vct-seg-loading">Loading segments…</div>
            ) : (
              <>
                <div className="vct-seg-list">
                  {populationSegments.map(seg => {
                    const checked = isSegmentChecked(seg.segment_name);
                    const subs = subSelections[seg.segment_name] || [];
                    const narrowed = checked && subs.length > 0;
                    return (
                      <div
                        key={seg.segment_id}
                        className={`vct-seg-card ${checked ? 'vct-seg-card--checked' : ''} ${narrowed ? 'vct-seg-card--narrowed' : ''}`}
                      >
                        <label className="vct-seg-card__head">
                          <input
                            type="checkbox"
                            className="vct-cbx-input"
                            checked={checked}
                            onChange={() => toggleSegment(seg.segment_name)}
                          />
                          <span className="vct-cbx" aria-hidden="true"><Icon.check /></span>
                          <div className="vct-seg-card__title">
                            <div className="vct-choice__label">{seg.segment_name}</div>
                            {seg.segment_description && (
                              <div className="vct-choice__desc">{seg.segment_description}</div>
                            )}
                          </div>
                          <span className="vct-choice__meta">
                            {countLoading && checked
                              ? <LoadingDots />
                              : `${segmentBreakdown[seg.segment_name] ?? seg.agent_count} agents`}
                          </span>
                        </label>

                        {checked && seg.sub_segments.length > 0 && (
                          <div className="vct-chips">
                            {seg.sub_segments.map(ss => {
                              const on = subs.includes(ss.sub_segment_name);
                              return (
                                <button
                                  key={ss.sub_segment_id}
                                  type="button"
                                  className={`vct-chip ${on ? 'vct-chip--selected' : ''}`}
                                  onClick={() => toggleSubSegment(seg.segment_name, ss.sub_segment_name)}
                                  title={ss.sub_segment_description || ''}
                                >
                                  {ss.sub_segment_name}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {checked && seg.distinction_clarity && Object.keys(seg.distinction_clarity).length > 0 && (
                          <div className="vct-chips" style={{ marginTop: 6 }}>
                            <span className="vct-choice__desc" style={{ alignSelf: 'center', marginRight: 4 }}>
                              Distinction:
                            </span>
                            {Object.keys(seg.distinction_clarity).map((key) => {
                              const on = (claritySelections[seg.segment_name] || []).includes(key);
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className={`vct-chip ${on ? 'vct-chip--selected' : ''}`}
                                  onClick={() => toggleClarity(seg.segment_name, key)}
                                  title="Filter this segment by how clearly respondents separate jerky from meat sticks"
                                >
                                  {key.charAt(0).toUpperCase() + key.slice(1)}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="vct-seg-summary">
                  {countLoading
                    ? <span>Counting matched agents <LoadingDots /></span>
                    : (() => {
                        // Exact count from the filter-count API; fall back to the estimate.
                        const shown = matchedCount ?? selectedAgentCount;
                        if (allAgents) return `Running all ${totalAgents ?? shown} agents`;
                        return `Running ${shown}${totalAgents ? ` of ${totalAgents}` : ''} agent${shown === 1 ? '' : 's'}`;
                      })()}
                </div>
              </>
            )}

            <OkRow onClick={goNext} onBack={goPrev} disabled={!canProceedSegments} />
          </div>
        </div>
      )}

      {/* ═══ REVIEW SCREEN ═══ */}
      {step === 'review' && (
        <div className="vct-screen vct-screen-enter">
          <div className="vct-screen__inner">
            <QNumber index={qIdx} total={totalQs} />
            <h1 className="vct-q-title">Ready to <em>launch?</em></h1>
            <p className="vct-q-help">Review the details below and click "Launch test" to start.</p>

            <div className="vct-review-card">
              <div className="vct-review-row">
                <div className="vct-review-row__label">Simulation</div>
                <div className="vct-review-row__value">
                  <strong style={{ color: 'var(--ink)' }}>{simName || 'Untitled'}</strong>
                </div>
                <button className="vct-review-row__edit" onClick={() => handleEdit('name')}>
                  <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                </button>
              </div>
              <div className="vct-review-row">
                <div className="vct-review-row__label">Concepts</div>
                <div className="vct-review-row__value">
                  {concepts.filter(c => c.conceptStr.trim()).map((c, i) => {
                    const stripMd = (s: string) => s.replace(/\*+/g, '').trim();
                    const m = c.conceptStr.match(/Brand Name:\**\s*(.+?)(?:\n|$)/);
                    const h = c.conceptStr.match(/Product Headline:\**\s*(.+?)(?:\n|$)/);
                    const label = (h && stripMd(h[1])) || (m && stripMd(m[1])) || `Concept ${i + 1}`;
                    return (
                      <span key={i} className="vct-concept-chip">
                        <span className="vct-concept-chip__num">{i + 1}</span>
                        {label.length > 36 ? label.slice(0, 36) + '…' : label}
                      </span>
                    );
                  })}
                </div>
                <button className="vct-review-row__edit" onClick={() => handleEdit('concept')}>
                  <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                </button>
              </div>
              <div className="vct-review-row">
                <div className="vct-review-row__label">Audience</div>
                <div className="vct-review-row__value">
                  {selectionSummary.length === 0
                    ? <span style={{ color: 'var(--muted)', fontWeight: 400 }}>None selected</span>
                    : selectionSummary.map((s, i) => (
                        <span key={s} style={{ color: 'var(--ink-soft)' }}>
                          {s}{i < selectionSummary.length - 1 ? ' · ' : ''}
                        </span>
                      ))}
                </div>
                {/* No Edit when there is no segments step to edit. Without this the summit cohort
                    could reach the full picker from Review, narrow the run and launch it — the exact
                    invisible narrowing this change exists to prevent, via an ordinary click. Review
                    finding on this PR. */}
                {hasSegments && (
                  <button className="vct-review-row__edit" onClick={() => handleEdit('segments')}>
                    <Icon.edit style={{ width: 13, height: 13 }} /> Edit
                  </button>
                )}
              </div>
              <div className="vct-review-row">
                <div className="vct-review-row__label">Sample size</div>
                <div className="vct-review-row__value">
                  <strong style={{ color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>
                    {totalProfiles}
                  </strong>
                  <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>
                    simulated consumers
                  </span>
                </div>
              </div>
            </div>

            <div className="vct-ok-row">
              <button type="button" className="vct-back-btn" onClick={goPrev}>
                <Icon.arrowLeft />
                Back
              </button>
              <button
                className="vct-ok-btn"
                onClick={goNext}
                disabled={!simName.trim()}
                style={{
                  padding: '15px 26px',
                  fontSize: 17,
                  opacity: !simName.trim() ? 0.5 : 1,
                  cursor: !simName.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                Launch test
                <Icon.sparkles style={{ width: 16, height: 16 }} />
              </button>
              {simName.trim() && !submitError && (
                <span className="vct-enter-hint">press <span className="vct-kbd">Enter ↵</span></span>
              )}
            </div>
            {submitError && (
              <div role="alert" style={{ marginTop: 12, fontSize: 13, color: 'var(--error)', textAlign: 'center' }}>
                {submitError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step nav — the chevrons are a shortcut for people who notice them; the primary
          Back/forward pair lives inline with the OK button (see OkRow). */}
      <div className="vct-step-nav">
        <span className="vct-step-counter"><strong>{qIdx}</strong> / {totalQs}</span>
        <div className="vct-step-nav__btns">
          <button className="vct-step-nav__btn" onClick={goPrev} disabled={!canGoPrev}>
            <Icon.chevronUp />
          </button>
          <button className="vct-step-nav__btn" onClick={goNext} disabled={!canGoNext}>
            <Icon.chevronDown />
          </button>
        </div>
      </div>

      {/* Sample Modal */}
      {showSample && <ConceptSampleModal onClose={() => setShowSample(false)} />}

      {/* Manual Editor Modal */}
      {showManualEntry && concepts[conceptIndex] && (
        <ConceptManualModal
          initialValue={concepts[conceptIndex].conceptStr || CONCEPT_TEMPLATE}
          requireEdit
          onSave={(value) => {
            const brandMatch = value.match(/Brand Name:\**\s*(.+?)(?:\n|$)/);
            const brandName = brandMatch ? brandMatch[1].replace(/\*+/g, '').trim() : '';
            updateConcept(conceptIndex, { conceptStr: value, brandName });
            setShowManualEntry(false);
          }}
          onClose={() => setShowManualEntry(false)}
        />
      )}
    </div>
  );
};

export default VitaminConceptTestForm;
